<script setup lang="ts">
import { asyncComputed, isNumber } from '@vueuse/core'
import bar from '@/charts/country/bar'

const props = defineProps<{
    dimension: Dimension
    country: Country
}>()

/**
 * Translations.
 */
const { t } = useI18n()

/**
 * Use query params.
 */
const { config } = useParams()

/**
 * Use country values.
 */
const { fetchData } = useValues()

/**
 * Use framework.
 */
const { findChildren } = useFramework()

/**
 * Factors.
 */
const factors = computed(() => {
    return findChildren(props.dimension.id)
})

/**
 * Factors data.
 */
const data = asyncComputed(async () => {
    const countries = [config.country, config.compareCountry].filter(Boolean)
    return await fetchData(unref(factors), countries)
})

/**
 * Generate the bar chart options.
 */
const chartOptions = asyncComputed(async () => {
    return bar(unref(data), unref(factors), props.dimension, false)
}, {})

/**
 * Chart container.
 */
const container = ref<HTMLDivElement>()

/**
 * Get the chart instance.
 */
const { instance } = useChart(container, chartOptions)

const noData = computed(() => {
    return unref(data)?.length && unref(data).every(d => !isNumber(d.value))
})
</script>

<template>
    <CountryCard
        :dimension="dimension"
        :country="country"
        :instance="instance"
        :title="t('factors')"
        class="max-sm:!aspect-[1/2]"
    >
        <UiLoader v-if="!data" />
        <UiMessage v-else-if="noData">
            {{ t('no-data-available') }}
        </UiMessage>
        <div v-else ref="container" class="h-full" />
    </CountryCard>
</template>